import { useMutation } from '@apollo/client';
import imageCompression from 'browser-image-compression';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import { Alert, Muted, Stack } from '../../components/ui';
import { GET_PRESIGNED_URLS } from '../../services/carService/queries';
import { useUpdateWorkerEquipmentPhotosMutation } from '../../services/serviceService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { serviceErrorMessage } from './serviceErrors';
import type { ProviderWorker } from './workerForm';

export const MIN_EQUIPMENT_PHOTOS = 3;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const Grid = styled.div`
  ${tw`grid grid-cols-2 gap-3 sm:grid-cols-3`}
`;

const Tile = styled.figure`
  ${tw`relative m-0 overflow-hidden`}
  aspect-ratio: 4 / 3;
  border-radius: var(--radius-card, 12px);
  background: var(--color-bg-secondary);
  img {
    ${tw`object-cover w-full h-full`}
  }
`;

const RemoveButton = styled.button`
  ${tw`absolute px-2 py-1 text-xs font-medium cursor-pointer`}
  right: 8px;
  bottom: 8px;
  border-radius: 9999px;
  border: none;
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary);
  box-shadow: var(--shadow-xs);
`;

const HiddenFileInput = styled.input`
  ${tw`hidden`}
`;

/** D32: long side 1,600 px, about 300 KB. */
async function compress(file: File): Promise<File> {
  try {
    const result = await imageCompression(file, {
      maxSizeMB: 0.3,
      maxWidthOrHeight: 1600,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 0.7,
    });
    return new File([result], file.name, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

/**
 * Photos of the tools a worker brings (spec 019 W6 step 5), at least 3 to
 * publish. Files go to S3 through the listing's presigned URLs, then
 * `updateWorkerEquipmentPhotos` saves the ordered list.
 */
export function EquipmentPhotosEditor({ worker }: { worker: ProviderWorker }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [getPresignedUrls] = useMutation(GET_PRESIGNED_URLS);
  const [updatePhotos, { loading: saving }] = useUpdateWorkerEquipmentPhotosMutation();
  const photos = worker.worker.equipmentPhotos ?? [];

  const save = async (keys: string[]) => {
    setError(null);
    try {
      await updatePhotos({ variables: { workerCarId: worker.carId, keys } });
    } catch (e) {
      setError(serviceErrorMessage(e, 'Could not save the photos'));
    }
  };

  const onFiles = async (files: FileList | null) => {
    const picked = Array.from(files ?? []).filter(
      file => file.type.startsWith('image/') && file.size <= MAX_FILE_BYTES,
    );
    if (fileInput.current) fileInput.current.value = '';
    if (!picked.length) {
      setError('Choose image files of 10 MB or less');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const { data } = await getPresignedUrls({
        variables: { fileType: 'image/jpeg', carId: worker.carId, count: picked.length },
      });
      const targets: { url: string; key: string }[] = data?.getPresignedUrls ?? [];
      const keys = await Promise.all(
        picked.map(async (file, index) => {
          const body = await compress(file);
          const response = await fetch(targets[index].url, {
            method: 'PUT',
            headers: { 'Content-Type': 'image/jpeg' },
            body,
          });
          if (!response.ok) throw new Error(`Upload failed (${response.status})`);
          return targets[index].key;
        }),
      );
      await save([...photos, ...keys]);
    } catch (e) {
      setError(serviceErrorMessage(e, 'Could not upload the photos'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack $gap={4}>
      {error && (
        <Alert tone="error" title="Photos">
          {error}
        </Alert>
      )}
      <Muted $size="sm" data-testid="equipment-photo-count">
        {photos.length < MIN_EQUIPMENT_PHOTOS
          ? `${photos.length} of ${MIN_EQUIPMENT_PHOTOS} photos. Add ${MIN_EQUIPMENT_PHOTOS - photos.length} more to publish.`
          : `${photos.length} photos`}
      </Muted>
      {photos.length > 0 && (
        <Grid>
          {photos.map((photo, index) => (
            <Tile key={`${photo}-${index}`}>
              <img src={toPublicS3ObjectUrl(photo)} alt={`Equipment ${index + 1}`} loading="lazy" />
              <RemoveButton
                type="button"
                disabled={saving}
                onClick={() => save(photos.filter((_, i) => i !== index))}
              >
                Remove
              </RemoveButton>
            </Tile>
          ))}
        </Grid>
      )}
      <HiddenFileInput
        ref={fileInput}
        type="file"
        accept="image/*"
        multiple
        onChange={event => onFiles(event.target.files)}
        data-testid="equipment-photo-input"
      />
      <div>
        <Button
          type="button"
          theme="secondary"
          text={uploading ? 'Uploading…' : 'Add photos'}
          isLoading={uploading}
          onClick={() => fileInput.current?.click()}
        />
      </div>
    </Stack>
  );
}
